// ============================================================================
// elbiralandok.js — "ELBÍRÁLANDÓ JAVASLATOK" áttekintés (2026-10-19, v113; a titkár #33 / #35 kérése): azok a hozzászólások, amelyeken nyitott
// fordításjavaslat vár, és amelyeket a tag el is bírálhat. Csak kliensoldali nézet: a hozzászólásokat (hasOpenSuggestions jelző) és a témákat a tag
// amúgy is olvashatja; a döntést a szerver hozza (translationSuggestion).
//
// Ki bírálhat el egy javaslatot? (a szerver szabályai szerint) a hozzászólás szerzője, a téma nyitója, az admin / témafelelős. A saját javaslatát
// a tag nem bírálhatja el (csak az admin / témafelelős) - de a SOR akkor is megjelenik, ha van rajta más által tett javaslat.
// ============================================================================

import { temaCim } from "./forumData.js";

const SOR_MAX_KARAKTER = 110;

/** Egy hozzászólás nyitott javaslatai nyelvenként: [{ lang, n }] (csak a szabályos, nem üres listák), a nyelvkód szerint rendezve. */
export function nyitottNyelvek(msg) {
  const terkep = msg && msg.openSuggestions && typeof msg.openSuggestions === "object" && !Array.isArray(msg.openSuggestions) ? msg.openSuggestions : {};
  return Object.entries(terkep)
    .filter(([, lista]) => Array.isArray(lista) && lista.filter((x) => typeof x === "string").length > 0)
    .map(([lang, lista]) => ({ lang, n: lista.filter((x) => typeof x === "string").length }))
    .sort((a, b) => a.lang.localeCompare(b.lang));
}

/** A szöveg rövid részlete egy sorba (a sortörések szóközök, a hosszú szöveg "…"-tal zárul). */
export function reszlet(szoveg, max = SOR_MAX_KARAKTER) {
  const s = String(szoveg || "").replace(/\s+/g, " ").trim();
  const k = [...s];
  return k.length <= max ? s : k.slice(0, max - 1).join("") + "…";
}

/**
 * Az elbírálandó javaslatokat tartalmazó sorok.
 * @param {Array<{roomId: string, archived?: boolean}>} szobak a tag szobái (csak ezek jelennek meg)
 * @param {Object<string, {uzenetek: Array, temak: Array}>} adatSzobankent roomId -> a nyitott javaslatos hozzászólások és a szoba témái
 * @param {string} uid a nézőé
 * @param {boolean} staff admin vagy témafelelős (minden javaslatot elbírálhat)
 * @returns {Array<{roomId, topicId, messageId, szoveg, nyelvek, osszes, tema, sajatJavaslatIs, utolso}>} a legfrissebb elöl
 */
export function elbiralandoSorok(szobak, adatSzobankent, uid, staff = false) {
  const lathato = new Set((szobak || []).filter((r) => r && !r.archived).map((r) => r.roomId));
  const sorok = [];
  for (const [roomId, adat] of Object.entries(adatSzobankent || {})) {
    if (!lathato.has(roomId) || !adat) continue;
    const temak = Array.isArray(adat.temak) ? adat.temak : [];
    for (const msg of Array.isArray(adat.uzenetek) ? adat.uzenetek : []) {
      if (!msg || msg.deleted === true || !msg.messageId) continue;
      const nyelvek = nyitottNyelvek(msg);
      if (nyelvek.length === 0) continue;
      const tema = typeof msg.topicId === "string" && msg.topicId ? temak.find((x) => x && x.topicId === msg.topicId) || null : null;
      const szerzo = msg.senderId === uid;
      const nyito = Boolean(tema) && tema.createdBy === uid;
      if (!staff && !szerzo && !nyito) continue; // nem ő bírálhatja el
      // Ha MINDEN nyitott javaslat az övé (és nem staff), akkor nincs mit elbírálnia: a saját javaslatát csak visszavonhatja.
      const sajatMind = !staff && nyelvek.every((n) => (msg.openSuggestions[n.lang] || []).every((x) => x === uid));
      if (sajatMind) continue;
      const ms = (ts) => (ts && typeof ts.toMillis === "function" ? ts.toMillis() : typeof ts === "number" ? ts : 0);
      sorok.push({
        roomId,
        topicId: msg.topicId || null,
        messageId: msg.messageId,
        szoveg: reszlet(msg.text),
        nyelvek,
        osszes: nyelvek.reduce((a, n) => a + n.n, 0),
        tema,
        sajatJavaslatIs: nyelvek.some((n) => (msg.openSuggestions[n.lang] || []).includes(uid)),
        utolso: ms(msg.updatedAt) || ms(msg.createdAt),
      });
    }
  }
  return sorok.sort((a, b) => b.utolso - a.utolso || a.messageId.localeCompare(b.messageId));
}

/** A sor témájának címe (a megjelenítés nyelvén); téma nélküli hozzászólásnál üres. */
export function sorTemaCim(sor, contentLang) {
  return sor && sor.tema ? temaCim(sor.tema, contentLang) : "";
}
